import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { Switch } from '@apps/web/components/ui/switch';
import { type components } from '@libraries/typescript/api_client';

type Settings = components['schemas']['AlayaCareSyncSettingsRead'];
type Changes = components['schemas']['AlayaCareSyncSettingsUpdate'];
type Gate = Exclude<keyof Changes, 'care_plan_document_filename_pattern'>;

const controls: ReadonlyArray<{
    key: Gate;
    label: string;
    description: string;
    internalOnly?: boolean;
}> = [
    {
        key: 'shift_creation_write_enabled',
        label: 'Create shifts',
        description: 'Create one-off visits in AlayaCare.',
        internalOnly: true,
    },
    {
        key: 'time_update_write_enabled',
        label: 'Move shifts and update times',
        description: 'Update the date and times of one visit.',
    },
    {
        key: 'assignment_write_enabled',
        label: 'Assign caregivers',
        description:
            'Write caregiver assignments and outreach fills to AlayaCare.',
    },
    {
        key: 'unassignment_write_enabled',
        label: 'Remove assigned caregivers',
        description: 'Reopen visits by removing their assigned caregiver.',
    },
    {
        key: 'cancellation_write_enabled',
        label: 'Cancel shifts',
        description:
            'Cancel visits using the configured AlayaCare cancellation reason.',
    },
    {
        key: 'note_write_enabled',
        label: 'Write shift notes',
        description: 'Save shift notes in AlayaCare.',
    },
    {
        key: 'realtime_clock_writeback_enabled',
        label: 'Clock in and out now',
        description:
            'Record the current AlayaCare server time for a linked, assigned visit.',
        internalOnly: true,
    },
    {
        key: 'clock_writeback_enabled',
        label: 'Historical clock corrections',
        description:
            'Write exact clock times. Requires separate AlayaCare permissions and verification.',
        internalOnly: true,
    },
    {
        key: 'shift_deletion_write_enabled',
        label: 'Delete agent-created shifts',
        description:
            'Allow permanent cleanup of eligible one-off visits created by the agent.',
        internalOnly: true,
    },
];

export function AlayaCareSettingsSection({
    settings,
    changes,
    onChange,
    disabled,
    organizationId,
}: {
    settings: Settings;
    changes: Changes;
    onChange: (changes: Changes) => void;
    disabled: boolean;
    organizationId: string;
}) {
    const internal = settings.sync_transport === 'internal';
    const filenamePattern =
        changes.care_plan_document_filename_pattern !== undefined
            ? changes.care_plan_document_filename_pattern
            : settings.care_plan_document_filename_pattern;
    return (
        <section className="space-y-4" aria-label="AlayaCare settings">
            <h4 className="text-sm font-medium">AlayaCare</h4>
            <p className="text-muted-foreground text-xs">
                Reads are {settings.read_enabled ? 'enabled' : 'disabled'}.
                Account switches allow writes; organization permissions and
                AlayaCare permissions must also allow the operation. Turning on
                a switch does not verify a live write.
            </p>
            <div className="space-y-1 rounded-md border p-3 text-xs">
                <p>
                    Organization shift creation:{' '}
                    {settings.shift_creation_enabled_for_org ? 'on' : 'off'}
                </p>
                <p>
                    Organization time changes:{' '}
                    {settings.time_updates_enabled_for_org ? 'on' : 'off'}
                </p>
                <p>
                    Organization clock-in:{' '}
                    {settings.clock_in_enabled_for_org ? 'on' : 'off'};
                    clock-out:{' '}
                    {settings.clock_out_enabled_for_org ? 'on' : 'off'}
                </p>
                <a
                    className="underline"
                    href={`/admin/organizations/${organizationId}`}
                >
                    Open organization settings
                </a>
            </div>
            {controls.map(({ key, label, description, internalOnly }) => {
                const checked = changes[key] ?? settings[key];
                const cannotEnable =
                    !settings.read_enabled || (internalOnly && !internal);
                return (
                    <div
                        key={key}
                        className="flex items-start justify-between gap-4"
                    >
                        <div className="space-y-1">
                            <Label htmlFor={`alayacare-${key}`}>{label}</Label>
                            <p className="text-muted-foreground text-xs">
                                {description}
                                {internalOnly && !internal
                                    ? ' Requires the internal portal connection.'
                                    : ''}
                            </p>
                        </div>
                        <Switch
                            id={`alayacare-${key}`}
                            aria-label={label}
                            checked={checked}
                            disabled={disabled || (!checked && cannotEnable)}
                            onCheckedChange={(value) =>
                                onChange({ ...changes, [key]: value })
                            }
                        />
                    </div>
                );
            })}
            <div className="space-y-2 border-t pt-4">
                <Label htmlFor="alayacare-document-pattern">
                    Care-plan document filename rule
                </Label>
                <Input
                    id="alayacare-document-pattern"
                    value={filenamePattern ?? ''}
                    maxLength={512}
                    disabled={disabled || !internal}
                    placeholder="Leave blank to disable document imports"
                    onChange={(event) =>
                        onChange({
                            ...changes,
                            care_plan_document_filename_pattern:
                                event.target.value.trim()
                                    ? event.target.value
                                    : null,
                        })
                    }
                />
                <p className="text-muted-foreground text-xs">
                    A Python regular expression matched against the whole
                    filename. Blank disables document acquisition. Only clients
                    within the account’s existing filters are considered;
                    patient identity checks still apply.
                </p>
                <p className="text-muted-foreground text-xs">
                    Organization care-plan imports:{' '}
                    {settings.document_import_enabled ? 'on' : 'off'}. Both this
                    organization setting and a filename rule are required.
                    Routine client care-plan data does not require this document
                    rule.
                </p>
            </div>
        </section>
    );
}
